'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Description as DocumentIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '@/services/api';

interface BusinessSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  documentId: number;
  documentName: string;
}

interface TaskStatus {
  task_id: string;
  task_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  progress_message: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  result_data?: {
    log_ids?: number[];
    total_files?: number;
  };
}

const BusinessSummaryDialog: React.FC<BusinessSummaryDialogProps> = ({
  open,
  onClose,
  projectId,
  documentId,
  documentName
}) => {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [reportContent, setReportContent] = useState<string>('');
  const [polling, setPolling] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0); // 0: 初始, 1: 生成中, 2: 完成, 3: 失败

  // 重置状态
  const resetState = () => {
    setTaskId(null);
    setTaskStatus(null);
    setLoading(false);
    setError('');
    setReportContent('');
    setPolling(false);
    setStep(0);
  };

  // 关闭对话框
  const handleClose = () => {
    if (polling) {
      // 如果正在轮询，确认是否关闭
      if (window.confirm('任务正在运行中，确定要关闭吗？')) {
        setPolling(false);
        resetState();
        onClose();
      }
    } else {
      resetState();
      onClose();
    }
  };

  // 生成业务汇总报告
  const generateSummary = async () => {
    setLoading(true);
    setError('');
    setStep(1);

    try {
      const response = await apiClient.businessSummary.generate(projectId);

      if (response.success && response.data?.task_id) {
        const newTaskId = response.data.task_id;
        setTaskId(newTaskId);
        setPolling(true);
        // 开始轮询任务状态
        pollTaskStatus(newTaskId);
      } else {
        setError('创建任务失败：' + (response.message || '未知错误'));
        setStep(3);
        setLoading(false);
      }
    } catch (err: any) {
      setError('创建任务失败：' + (err.message || '未知错误'));
      setStep(3);
      setLoading(false);
    }
  };

  // 轮询任务状态
  const pollTaskStatus = async (currentTaskId: string) => {
    try {
      const response = await apiClient.businessSummary.getTaskStatus(currentTaskId);

      if (response.success && response.data) {
        const status: TaskStatus = response.data;
        setTaskStatus(status);

        // 更新进度
        if (status.progress !== undefined) {
          setTaskStatus(status);
        }

        // 检查任务是否完成
        if (status.status === 'completed') {
          setPolling(false);
          setStep(2);
          setLoading(false);

          // 获取生成的报告
          if (status.result_data?.log_ids && status.result_data.log_ids.length > 0) {
            await fetchReport(status.result_data.log_ids[0]);
          }
        } else if (status.status === 'failed') {
          setPolling(false);
          setStep(3);
          setLoading(false);
          setError(status.error_message || '任务执行失败');
        } else {
          // 继续轮询
          if (polling) {
            setTimeout(() => pollTaskStatus(currentTaskId), 2000);
          }
        }
      } else {
        setError('获取任务状态失败');
        setPolling(false);
        setStep(3);
        setLoading(false);
      }
    } catch (err: any) {
      setError('获取任务状态失败：' + (err.message || '未知错误'));
      setPolling(false);
      setStep(3);
      setLoading(false);
    }
  };

  // 获取生成的报告
  const fetchReport = async (logId: number) => {
    try {
      const response = await apiClient.projectLogs.getById(projectId, logId);

      if (response.success && response.data?.content) {
        setReportContent(response.data.content);
      } else {
        setError('获取报告内容失败');
      }
    } catch (err: any) {
      setError('获取报告内容失败：' + (err.message || '未知错误'));
    }
  };

  // 重试任务
  const handleRetry = () => {
    if (taskId) {
      setStep(1);
      setLoading(true);
      setError('');
      setPolling(true);

      apiClient.businessSummary.retryTask(taskId)
        .then(() => {
          setTimeout(() => pollTaskStatus(taskId!), 2000);
        })
        .catch((err) => {
          setError('重试失败：' + (err.message || '未知错误'));
          setStep(3);
          setLoading(false);
          setPolling(false);
        });
    }
  };

  // 步骤配置
  const steps = ['初始化', '生成报告中', '查看报告'];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon color="primary" />
          <Typography variant="h6">业务汇总报告</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* 文档信息 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            分析文档
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon fontSize="small" color="action" />
            <Typography variant="body1">{documentName}</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* 步骤指示器 */}
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={step} alternativeLabel>
            <Step>
              <StepLabel>初始化</StepLabel>
            </Step>
            <Step>
              <StepLabel>生成报告</StepLabel>
            </Step>
            <Step>
              <StepLabel>查看报告</StepLabel>
            </Step>
          </Stepper>
        </Box>

        {/* 初始状态 */}
        {step === 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <AnalyticsIcon sx={{ fontSize: 80, color: 'primary.main', opacity: 0.5 }} />
            <Typography variant="h6" color="textSecondary">
              准备生成业务汇总报告
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center" sx={{ maxWidth: 400 }}>
              该功能会自动分析业务数据，识别关键指标、异常值和业务洞察，生成有价值的汇总报告。
            </Typography>
          </Box>
        )}

        {/* 生成中状态 */}
        {step === 1 && (
          <Box sx={{ flex: 1 }}>
            {taskStatus && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  任务进度
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={taskStatus.progress || 0}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="textSecondary">
                  {taskStatus.progress_message || '正在处理...'}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Chip
                    label={`状态: ${taskStatus.status}`}
                    color={taskStatus.status === 'running' ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`进度: ${taskStatus.progress || 0}%`}
                    size="small"
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" color="textSecondary">
                正在生成业务汇总报告...
              </Typography>
              <Typography variant="body2" color="textSecondary">
               这可能需要几分钟时间，请耐心等待
              </Typography>
            </Box>
          </Box>
        )}

        {/* 完成状态 - 显示报告 */}
        {step === 2 && reportContent && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6" color="success.main">
                  报告生成成功！
                </Typography>
              </Box>
              <Tooltip title="重新生成">
                <IconButton
                  size="small"
                  onClick={() => {
                    resetState();
                    generateSummary();
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                bgcolor: 'background.default'
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({children}) => <Typography variant="h4" gutterBottom sx={{ mt: 2, mb: 1 }}>{children}</Typography>,
                  h2: ({children}) => <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 1 }}>{children}</Typography>,
                  h3: ({children}) => <Typography variant="h6" gutterBottom sx={{ mt: 1.5, mb: 0.5 }}>{children}</Typography>,
                  p: ({children}) => <Typography paragraph>{children}</Typography>,
                  ul: ({children}) => <Box component="ul" sx={{ pl: 2 }}>{children}</Box>,
                  ol: ({children}) => <Box component="ol" sx={{ pl: 2 }}>{children}</Box>,
                  li: ({children}) => <Typography component="li" variant="body1">{children}</Typography>,
                  strong: ({children}) => <Typography component="strong" variant="inherit" sx={{ fontWeight: 'bold' }}>{children}</Typography>,
                }}
              >
                {reportContent}
              </ReactMarkdown>
            </Paper>
          </Box>
        )}

        {/* 失败状态 */}
        {step === 3 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main' }} />
            <Typography variant="h6" color="error">
              生成失败
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 500 }}>
                {error}
              </Alert>
            )}

            {taskStatus?.error_message && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 500 }}>
                {taskStatus.error_message}
              </Alert>
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleRetry}
                startIcon={<RefreshIcon />}
              >
                重试
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  resetState();
                }}
              >
                返回
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 0 && (
          <>
            <Button onClick={handleClose}>
              取消
            </Button>
            <Button
              variant="contained"
              onClick={generateSummary}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AnalyticsIcon />}
            >
              {loading ? '启动中...' : '生成报告'}
            </Button>
          </>
        )}

        {step === 2 && (
          <Button onClick={handleClose} variant="contained">
            关闭
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BusinessSummaryDialog;
